import {
  addDays,
  getISODay,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  startOfMonth,
  startOfToday,
  toDate
} from 'date-fns'
import {addMonths, addYears} from 'date-fns/fp'
import React, {useEffect, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Time} from 'yougile-util'
import {RangePoint} from 'yougile-model'
import {BgOpacityContainer} from '@components/common/bg-opacity-container/bg-opacity-container'
import {cn} from '@containers/html-utils'
import type {DateRange} from './date-picker'
import NextMonthSVG from './svg/next-month.svg'
import NextYearSVG from './svg/next-year.svg'

interface Props {
  dateRange: DateRange
  rangePoint: RangePoint
  onSelect: (date: Date) => void
  disablePastDates?: boolean
}

export const MonthCalendar = (props: Props) => {
  const {dateRange, rangePoint, onSelect, disablePastDates} = props
  const activeDate = dateRange[rangePoint]
  const initialCalendarDate = activeDate ?? startOfToday()
  const [calendarDate, setCalendarDate] = useState(initialCalendarDate)
  useEffect(() => {
    if (
      activeDate &&
      initialCalendarDate.getTime() !== calendarDate.getTime()
    ) {
      setCalendarDate(initialCalendarDate)
    }
  }, [initialCalendarDate])

  const renderDayCell = (date: Date, colIndex: number) => {
    const dayOfMonth = date.getDate()
    const dimmed = !isSameMonth(date, calendarDate)
    const now = toDate(Time.now())
    const today = isSameDay(date, now)
    const options = getCellDecorationOptions(date, dateRange, rangePoint)
    const bgClassName = cn(
      options.highlighted && 'bg-action-default',
      options.hlEnd && 'rounded-r-full',
      options.hlStart && 'rounded-l-full'
    )
    const disabled = disablePastDates && !today && date < now

    return (
      <div
        key={colIndex}
        className={cn('w-28 h-28 group', !disabled && 'cursor-pointer')}
        onClick={() => !disabled && onSelect(date)}
      >
        <BgOpacityContainer
          bgColorClassName={bgClassName}
          bgOpacityClassName="opacity-15"
        >
          <TwCell
            $dimmed={dimmed}
            $disabled={disabled}
            $selected={options.selected}
            $today={today}
          >
            {dayOfMonth}
          </TwCell>
        </BgOpacityContainer>
      </div>
    )
  }

  const grid = getMonthGrid(calendarDate)
  return (
    <div>
      <NavBar value={calendarDate} onChange={date => setCalendarDate(date)} />
      {grid.map((days, rowIndex) => (
        <div key={rowIndex} className="flex">
          {days.map(renderDayCell)}
        </div>
      ))}
    </div>
  )
}

interface NavBarProps {
  value: Date
  onChange: (date: Date) => void
}

const NavBar = (props: NavBarProps) => {
  const value = startOfMonth(props.value)
  const dispatch = (op: (date: Date) => Date) => () => props.onChange(op(value))
  const monthName = Lang.msg.monthNames[value.getMonth()]
  const monthTitle = `${monthName} ${value.getFullYear()}`
  return (
    <div className="flex pb-2">
      <TwNavButton $flip={true} onClick={dispatch(addYears(-1))}>
        <NextYearSVG />
      </TwNavButton>
      <TwNavButton $flip={true} onClick={dispatch(addMonths(-1))}>
        <NextMonthSVG />
      </TwNavButton>
      <div className="self-center flex-grow font-semibold text-center text-12 text-third-old">
        {monthTitle}
      </div>
      <TwNavButton onClick={dispatch(addMonths(1))}>
        <NextMonthSVG />
      </TwNavButton>
      <TwNavButton onClick={dispatch(addYears(1))}>
        <NextYearSVG />
      </TwNavButton>
    </div>
  )
}

const TwNavButton = tw.div<{$flip?: boolean}>`
  flex-none 
  flex
  cursor-pointer
  text-third-old
  ${p => cn(p.$flip && 'transform rotate-180')}
  hover:text-action-hover
`

const TwCell = tw.div<{
  $selected?: boolean
  $dimmed?: boolean
  $today?: boolean
  $disabled?: boolean
}>`
  w-28 
  h-28
  flex
  justify-center
  items-center
  rounded-full
  text-12
  border
  border-solid
  border-transparent
    ${p =>
      cn(
        !p.$disabled && 'group-hover:border-action-hover',
        (p.$disabled || p.$dimmed) && 'text-obscure-old',
        p.$selected && 'bg-action-default text-inverse-old font-semibold',
        p.$today ? 'text-accent-old' : 'text-secondary-old'
      )}
`

const getMonthGrid = (date: Date): Date[][] => {
  const startDate = startOfMonth(date)
  let weekNum = -1
  const calendar: Date[][] = []
  const weekStartOffset = 1 - getISODay(startDate)
  for (let amount = weekStartOffset; amount < 100; amount++) {
    const date = addDays(startDate, amount)
    const weekdayNum = getISODay(date)
    const ownMonth = isSameMonth(date, startDate)
    if (weekdayNum === 1) {
      weekNum++
      if (weekNum > 0 && !ownMonth) {
        break
      } else {
        calendar[weekNum] = []
      }
    }
    calendar[weekNum][weekdayNum - 1] = date
  }
  return calendar
}

interface CellDecorationOptions {
  selected?: boolean
  highlighted?: boolean
  hlStart?: boolean
  hlEnd?: boolean
}

const getCellDecorationOptions = (
  date: Date,
  {start, end}: DateRange,
  selectionRangePoint?: RangePoint
): CellDecorationOptions => {
  const atStart = start && isSameDay(date, start)
  const atEnd = end && isSameDay(date, end)
  const useStartRangePoint = selectionRangePoint !== 'end'
  const selected = useStartRangePoint ? atStart : atEnd
  const highlighted =
    start &&
    end &&
    (atStart || atEnd || (end >= start && isWithinInterval(date, {start, end})))
  return {selected, highlighted, hlStart: atStart, hlEnd: atEnd}
}
